import { computed, Inject, inject, Injectable, InjectionToken } from '@angular/core';
import { SearchParamsService } from '@educa/educa-ui';
import * as lzString from 'lz-string';

import { ResetService } from '@/core/services/reset.service';

export const AF_DEFAULT_FILTERS = new InjectionToken<Record<string, unknown>>('AF_DEFAULT_FILTERS');

@Injectable({
  providedIn: 'any'
})
export class AdvancedFiltersService<T extends Record<string, unknown>> {
  readonly defaultFilters: T;

  constructor(@Inject(AF_DEFAULT_FILTERS) defaultFilters: T) {
    this.defaultFilters = defaultFilters;
  }

  private readonly debug = false; // FOR DEBUGGING

  private readonly searchParamsService = inject(SearchParamsService);
  readonly resetService = inject(ResetService);

  readonly af = this.searchParamsService.getParam('af', ''); // AF = Advanced Filters

  readonly advancedFilterActive = computed<boolean>(() => {
    const defaultCompressed = this.compressFilters(JSON.stringify(this.defaultFilters));
    const afCompressed = this.af();

    return defaultCompressed !== afCompressed && afCompressed !== '';
  });

  readonly advancedFilters = computed<T>(() => {
    const decompressed = this.decompressFilters(this.af()); // AF = Advanced Filters
    if (!decompressed) return '';

    return JSON.parse(decompressed);
  });

  public clearAllFilters(): void {
    this.searchParamsService.setParams({ af: this.buildCompressedAdvancedFilters(this.defaultFilters), page: null });
    this.resetService.resetAdvancedFilters$.next();
  }

  public areFiltersEmpty(filters: T): boolean {
    return Object.values(filters).every(
      (filter) =>
        (Array.isArray(filter) && filter.length === 0) ||
        (filter && typeof filter === 'object' && Object.values(filter).every((x) => x == null)) ||
        filter == null
    );
  }

  public setAdvancedFilters(filters: T): void {
    const compressed = this.buildCompressedAdvancedFilters(this.areFiltersEmpty(filters) ? {} : filters);
    this.searchParamsService.setParams({ af: compressed, page: null });
  }

  public buildCompressedAdvancedFilters(filters: T | object): string {
    const cleanFilters = this.cleanFilters(filters);

    return this.compressFilters(JSON.stringify(cleanFilters));
  }

  public compressFilters(filters: string): string {
    if (!filters) return '';

    if (this.debug) {
      // eslint-disable-next-line no-console
      console.info('COMPRESS AdvancedFiltersService(debug):', lzString.compressToEncodedURIComponent(filters));
    }

    return lzString.compressToEncodedURIComponent(filters) || '';
  }

  private cleanFilters(filters: T | object): T | object {
    const cleanFilters = { ...filters };
    Object.keys(cleanFilters).forEach((key) => {
      if (
        cleanFilters[key] === undefined ||
        cleanFilters[key] === null ||
        cleanFilters[key] === '' ||
        (Array.isArray(cleanFilters[key]) && cleanFilters[key].length === 0)
      ) {
        delete cleanFilters[key];
      }
    });

    return cleanFilters;
  }

  private decompressFilters(compressed: string): string {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.info('DECOMPRESS AdvancedFiltersService(debug):', lzString.decompressFromEncodedURIComponent(compressed));
    }

    return lzString.decompressFromEncodedURIComponent(compressed) || '';
  }
}
